/**
 * Seasons - the Hub's holiday touches. Halloween shipped first (Bobbie,
 * 2026-08-17: "whimsical and cute, not spooky"), then the same day: "I want
 * to build a Thanksgiving Christmas Valentines St. Patrick's Day as well."
 * One system, five holidays: each is a date window plus a favicon, a
 * greeting emoji, avatar badges, and a confetti palette.
 *
 * Brand colors are NEVER touched by a season (Bobbie: "let's actually keep
 * the green and black"). What a season turns on is glyphs and garnish: the
 * emoji in the home greeting (HeroAndTasks.tsx), the browser-tab favicon,
 * the little badge on avatar bubbles (seasonalAvatarBadge.ts), holiday
 * confetti (celebrationConfetti.ts), and one soft accent on the greeting
 * moment only.
 *
 * The ROSTER and date logic live in seasonData.ts (pure, node-importable,
 * shared with the confetti seam); this file is the React layer and
 * re-exports the data API so components import from one place. The provider
 * stamps data-season="<key>" on <html> while a season is live; theme.css
 * reads that attribute for exactly one thing: the per-season accent token
 * blocks ([data-season='...'] defining --season-accent-*), which the few
 * opted-in decorative spots resolve with fallbacks.
 *
 * Preview override: /season-preview can force any one season on early via
 * localStorage, this browser only, independent of the real date. Clearing
 * the preview returns to the automatic date windows. Non-React code reads
 * the same override through activeSeason() in seasonData.ts, so previewed
 * confetti matches previewed everything else.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  SEASON_PREVIEW_STORAGE_KEY,
  readSeasonPreviewKey,
  seasonByKey,
  seasonForDate,
  type SeasonDef,
} from './seasonData';

export { SEASONS, seasonForDate, thanksgivingDayOf, type SeasonDef } from './seasonData';

const FAVICON_HREF_DEFAULT = '/favicon.svg';

function setFavicon(emoji: string | null) {
  if (typeof document === 'undefined') return;
  const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) return;
  if (emoji) {
    // A tiny inline SVG that just draws the emoji as text - no asset file
    // needed, and it reverts to the real favicon the moment the season ends.
    const svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>" +
      `<text x='50' y='78' font-size='72' text-anchor='middle'>${emoji}</text></svg>`;
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  } else {
    link.href = FAVICON_HREF_DEFAULT;
  }
}

const SeasonContext = createContext<{
  /** The live season (previewed or by date), or null most of the year. */
  season: SeasonDef | null;
  active: boolean;
  previewing: boolean;
  /** Force a season on (by key) for this browser, or null to stop. */
  setPreview: (key: string | null) => void;
}>({ season: null, active: false, previewing: false, setPreview: () => {} });

export function SeasonProvider({ children }: { children: React.ReactNode }) {
  const [previewKey, setPreviewKey] = useState<string | null>(readSeasonPreviewKey);
  const [dateKey, setDateKey] = useState<string | null>(
    () => seasonForDate(new Date())?.key ?? null
  );

  // Recompute on focus, so a browser left open across a window boundary
  // catches up without a reload - and re-read the preview key too, so a
  // preview toggled in one tab reaches this one when it regains focus.
  useEffect(() => {
    const recompute = () => {
      setDateKey(seasonForDate(new Date())?.key ?? null);
      setPreviewKey(readSeasonPreviewKey());
    };
    document.addEventListener('visibilitychange', recompute);
    window.addEventListener('focus', recompute);
    return () => {
      document.removeEventListener('visibilitychange', recompute);
      window.removeEventListener('focus', recompute);
    };
  }, []);

  const season = seasonByKey(previewKey) ?? seasonByKey(dateKey);

  useEffect(() => {
    if (season) {
      document.documentElement.setAttribute('data-season', season.key);
    } else {
      document.documentElement.removeAttribute('data-season');
    }
    setFavicon(season?.favicon ?? null);
  }, [season]);

  const setPreview = useCallback((key: string | null) => {
    const valid = seasonByKey(key)?.key ?? null;
    setPreviewKey(valid);
    try {
      if (valid) localStorage.setItem(SEASON_PREVIEW_STORAGE_KEY, valid);
      else localStorage.removeItem(SEASON_PREVIEW_STORAGE_KEY);
    } catch {
      // localStorage can throw in private mode; the in-memory state still applies.
    }
  }, []);

  return (
    <SeasonContext.Provider
      value={{ season, active: season != null, previewing: previewKey != null, setPreview }}
    >
      {children}
    </SeasonContext.Provider>
  );
}

/** The holiday touches showing right now (real date or preview). */
export function useSeason() {
  return useContext(SeasonContext);
}
